/**
 * Punch List Service
 * Handles punch list item management API calls
 */

import { httpClient, ApiResponse } from './http.client';

export interface PunchList {
  punch_item_id: string;
  organization_id: string;
  project_id: string;
  item_number: string;

  // Item Details
  title?: string;
  description: string;
  location: string;
  work_area?: string;
  trade?: string;
  category?: string;
  deficiency_type?: string;
  phase?: string;

  // Assignment
  assigned_to?: string;
  contractor_company?: string;

  // Status & Timeline
  status: 'open' | 'in-progress' | 'completed' | 'verified' | 'rejected' | 'on-hold';
  priority: 'low' | 'medium' | 'high' | 'critical';
  due_date?: string;
  completed_date?: string;

  // Progress
  work_performed?: string;
  completion_notes?: string;

  // Quality Control
  inspected_by?: string;
  inspection_date?: string;
  quality_verified?: boolean;
  reinspection_required?: boolean;

  // Impact Assessment
  cost_impact?: boolean;
  schedule_impact?: boolean;

  // Metadata
  identified_by: string;
  photos?: Array<{ file_id: string; file_path: string }>;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface PunchListCreate {
  organization_id: string;
  project_id: string;
  title?: string;
  description: string;
  location: string;
  work_area?: string;
  trade?: string;
  category?: string;
  deficiency_type?: string;
  phase?: string;
  assigned_to?: string;
  contractor_company?: string;
  priority?: 'low' | 'medium' | 'high' | 'critical';
  due_date?: string;
  cost_impact?: boolean;
  schedule_impact?: boolean;
  photos?: Array<{ file_id: string; file_path: string }>;
  notes?: string;
}

export interface PunchListUpdate {
  title?: string;
  description?: string;
  location?: string;
  work_area?: string;
  trade?: string;
  category?: string;
  deficiency_type?: string;
  phase?: string;
  assigned_to?: string;
  contractor_company?: string;
  status?: 'open' | 'in-progress' | 'completed' | 'verified' | 'rejected' | 'on-hold';
  priority?: 'low' | 'medium' | 'high' | 'critical';
  due_date?: string;
  completed_date?: string;
  work_performed?: string;
  completion_notes?: string;
  cost_impact?: boolean;
  schedule_impact?: boolean;
  photos?: Array<{ file_id: string; file_path: string }>;
  notes?: string;
}

export interface PunchListComplete {
  status: 'completed';
  work_performed: string;
  completion_notes?: string;
  quality_verified?: boolean;
}

export interface PunchListInspect {
  status: 'verified' | 'rejected';
  quality_verified?: boolean;
  reinspection_required?: boolean;
  completion_notes?: string;
}

class PunchListService {
  async getPunchListItems(page = 1, perPage = 50, filters?: any): Promise<ApiResponse<{ data: PunchList[]; total: number; page: number; per_page: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      ...filters
    });

    return httpClient.get<{ data: PunchList[]; total: number; page: number; per_page: number }>(
      `/api/v1/project-management/punch-lists?${queryParams}`
    );
  }

  async getProjectPunchListItems(projectId: string): Promise<ApiResponse<PunchList[]>> {
    return httpClient.get<PunchList[]>(`/api/v1/project-management/punch-lists?project_id=${projectId}`);
  }

  async getPunchListItem(punchId: string): Promise<ApiResponse<PunchList>> {
    return httpClient.get<PunchList>(`/api/v1/project-management/punch-lists/${punchId}`);
  }

  async createPunchListItem(punchData: PunchListCreate): Promise<ApiResponse<PunchList>> {
    return httpClient.post<PunchList>('/api/v1/project-management/punch-lists/', {
      ...punchData,
      status: 'open',
      priority: punchData.priority || 'medium'
    });
  }

  async updatePunchListItem(punchId: string, punchData: PunchListUpdate): Promise<ApiResponse<PunchList>> {
    return httpClient.put<PunchList>(`/api/v1/project-management/punch-lists/${punchId}`, punchData);
  }

  async deletePunchListItem(punchId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/project-management/punch-lists/${punchId}`);
  }

  async completePunchListItem(punchId: string, completeData: PunchListComplete): Promise<ApiResponse<PunchList>> {
    return httpClient.post<PunchList>(`/api/v1/project-management/punch-lists/${punchId}/complete`, completeData);
  }

  async inspectPunchListItem(punchId: string, inspectData: PunchListInspect): Promise<ApiResponse<PunchList>> {
    return httpClient.post<PunchList>(`/api/v1/project-management/punch-lists/${punchId}/inspect`, inspectData);
  }

  async getPunchListStats(): Promise<ApiResponse<{
    total_items: number;
    open_items: number;
    completed_items: number;
    overdue_items: number;
  }>> {
    return httpClient.get<{
      total_items: number;
      open_items: number;
      completed_items: number;
      overdue_items: number;
    }>('/api/v1/project-management/punch-lists/stats/summary');
  }

  async getNextItemNumber(projectId: string): Promise<ApiResponse<{ item_number: string }>> {
    return httpClient.get<{ item_number: string }>(`/api/v1/project-management/punch-lists/next-item-number?project_id=${projectId}`);
  }

}

export const punchListService = new PunchListService();